/**
 * POS PWA - Point of Sale Application
 * Domain: pos.chain29.com
 * 
 * Full-featured point of sale for:
 * - Order entry
 * - Payment processing
 * - Tables: the one floor (Restaurant OS, /dining/floor)
 * - Receipt printing
 */

import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ShoppingCart } from 'lucide-react';
import POSLayout from './POSLayout';
import { PwaAccountGate } from '@/components/pwa/PwaAccountGate';
import { useAccount } from '@/lib/pwaAccount';
import { posPath } from './posPath';

// Pages
import POSPage from './pages/POSPage';
import DrawerPage from './pages/DrawerPage';
import OrdersPage from './pages/OrdersPage';
import SettingsPage from './pages/SettingsPage';
import POSLoginFlow from './pages/POSLoginFlow';
import POSSetupFlow from './pages/POSSetupFlow';
import DiningApp from '../dining/DiningApp';

// Context providers
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';

// A cashier session belongs to the device's restaurant account, or it is dropped.
function staffOfAccount(account) {
  try {
    const staff = JSON.parse(localStorage.getItem('pos_user') || 'null');
    return !!staff && String(staff.restaurant_id) === String(account?.restaurant?.id);
  } catch (e) {
    return false;
  }
}

// Protected route: the restaurant account is signed in (PwaAccountGate), then a
// cashier of THAT restaurant (phone + PIN, /login). Live updates are the dining
// event stream's (lib/diningEvents) on the screens that need them.
const ProtectedRoute = ({ children }) => {
  const account = useAccount();
  const sameRestaurant = staffOfAccount(account);
  if (!sameRestaurant) {
    localStorage.removeItem('pos_token');
    localStorage.removeItem('pos_user');
  }
  const token = sameRestaurant ? localStorage.getItem('pos_token') : null;
  if (!token) {
    return <Navigate to={posPath('login')} replace />;
  }
  return children;
};

export default function POSApp() {
  const location = useLocation();
  // The PIN set-up link texted to a new cashier (one-time setup token, opened
  // on their own phone) is the only screen without the account sign-in.
  if (/\/setup\/?$/.test(location.pathname)) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <POSSetupFlow />
      </>
    );
  }
  return (
    <PwaAccountGate appName="Point of Sale" icon={ShoppingCart} accent="#10b981"
      subtitle="Sign this register in with your restaurant account (the same login as Back Office). Cashiers then sign in with their phone and PIN.">
    <AuthProvider>
      <CartProvider>
        <Toaster position="top-center" richColors />
        <Routes>
          {/* Public routes */}
          <Route path="login" element={<POSLoginFlow />} />
          <Route path="setup" element={<POSSetupFlow />} />
          {/* chain29 Restaurant OS (floor, order pad, KDS, expo, checkout): its own staff gate */}
          <Route path="dining/*" element={<DiningApp />} />
          
          {/* Protected routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <POSLayout>
                  <Routes>
                    <Route index element={<POSPage />} />
                    {/* the old table screens are retired: tables are the one floor */}
                    <Route path="tables" element={<Navigate to={posPath('dining/floor')} replace />} />
                    <Route path="table-order/*" element={<Navigate to={posPath('dining/floor')} replace />} />
                    <Route path="checkout/*" element={<Navigate to={posPath('dining/floor')} replace />} />
                    <Route path="orders" element={<OrdersPage />} />
                    <Route path="drawer" element={<DrawerPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="*" element={<Navigate to={posPath('')} replace />} />
                  </Routes>
                </POSLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </CartProvider>
    </AuthProvider>
    </PwaAccountGate>
  );
}
